import { useMemo } from 'react';

import {
  ItemTypeCategory,
  MerchantOrderTypeEnum,
} from '@/graphql/generatedTypes';

export interface EventItemTypeOption {
  item_type_id: number;
  name: string;
  imageUrl: string | null;
  hexCode: string | null;
  category: ItemTypeCategory;
}

/** Shape of `Event.allowed_item_types` as the event queries select it. */
export interface EventAllowedItemType {
  id: number;
  name: string;
  image_url?: string | null;
  color_hex_code?: string | null;
  category: ItemTypeCategory;
  archived?: boolean;
}

/**
 * The ItemTypes offerable as `items` lines on an event order: the event's
 * allow list (Event.allowed_item_types), narrowed by colugo's per-order-type
 * gate — CONTAINER_STATION (empty boxes) on buffer AND event account orders,
 * ASSET on buffer orders only. colugo enforces both on save.
 */
export function useEventItemTypeOptions({
  orderType,
  allowedItemTypes,
}: {
  orderType: MerchantOrderTypeEnum | null | undefined;
  allowedItemTypes: ReadonlyArray<EventAllowedItemType> | null | undefined;
}): {
  stationOptions: EventItemTypeOption[];
  assetOptions: EventItemTypeOption[];
} {
  const stationsAllowed =
    orderType === MerchantOrderTypeEnum.EventBufferOrder ||
    orderType === MerchantOrderTypeEnum.EventAccountOrder;
  const assetsAllowed = orderType === MerchantOrderTypeEnum.EventBufferOrder;

  const options = useMemo(
    () =>
      (allowedItemTypes ?? [])
        .filter((itemType) => !itemType.archived)
        .map(
          (itemType): EventItemTypeOption => ({
            item_type_id: itemType.id,
            name: itemType.name ?? `#${itemType.id}`,
            imageUrl: itemType.image_url ?? null,
            hexCode: itemType.color_hex_code ?? null,
            category: itemType.category,
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allowedItemTypes],
  );

  const stationOptions = useMemo(
    () =>
      stationsAllowed
        ? options.filter(
            (opt) => opt.category === ItemTypeCategory.ContainerStation,
          )
        : [],
    [stationsAllowed, options],
  );
  const assetOptions = useMemo(
    () =>
      assetsAllowed
        ? options.filter((opt) => opt.category === ItemTypeCategory.Asset)
        : [],
    [assetsAllowed, options],
  );

  return { stationOptions, assetOptions };
}
